"use client"

import React, { useEffect, useRef, useState } from "react"
import Link from "next/link"
import { ChevronDown, List, Mail, MessageCircle } from "lucide-react"

export interface LegalSection {
  id: string
  title: string
  html: string
}

export interface LegalPageProps {
  lng: string
  title: string
  subtitle?: string
  lastUpdated?: string
  lastUpdatedLabel?: string
  tocLabel?: string
  breadcrumbHome?: string
  breadcrumbCategory?: string
  breadcrumbCategoryHref?: string
  sections: LegalSection[]
  contact: {
    title: string
    subtitle: string
    liveChat: string
    liveChatNote: string
    whatsApp: string
    whatsAppValue: string
    email: string
    emailValue: string
  }
}

export default function LegalPageLayout({
  lng,
  title,
  subtitle,
  lastUpdated,
  lastUpdatedLabel,
  tocLabel,
  breadcrumbHome,
  breadcrumbCategory,
  breadcrumbCategoryHref,
  sections,
  contact,
}: LegalPageProps) {
  const [activeId, setActiveId] = useState<string>(sections[0]?.id ?? "")
  const [mobileTocOpen, setMobileTocOpen] = useState(false)
  const sectionRefs = useRef<Map<string, HTMLElement>>(new Map())

  // Scroll spy: highlight current section in TOC
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        // Find the topmost intersecting section
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)
        if (visible[0]) {
          setActiveId(visible[0].target.id)
        }
      },
      { rootMargin: "-100px 0px -60% 0px", threshold: 0 }
    )
    sectionRefs.current.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
  }, [sections])

  // Handle initial URL hash scroll
  useEffect(() => {
    const hash = window.location.hash.replace(/^#/, "")
    if (hash && sections.some((s) => s.id === hash)) {
      setTimeout(() => {
        document
          .getElementById(hash)
          ?.scrollIntoView({ behavior: "smooth", block: "start" })
      }, 100)
    }
  }, [sections])

  const waNumber = contact.whatsAppValue?.replace(/[^\d]/g, "") ?? ""

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section>
        <div className="mx-auto max-w-5xl px-5 pb-10 pt-8 md:px-10 md:pb-14 md:pt-12">
          <nav
            aria-label="Breadcrumb"
            className="mb-6 flex items-center gap-2 text-sm text-stone-400"
          >
            <Link
              href={`/${lng}`}
              className="transition-colors hover:text-stone-700"
            >
              {breadcrumbHome ?? "Home"}
            </Link>
            {breadcrumbCategory && (
              <>
                <span>/</span>
                {breadcrumbCategoryHref ? (
                  <Link
                    href={breadcrumbCategoryHref}
                    className="transition-colors hover:text-stone-700"
                  >
                    {breadcrumbCategory}
                  </Link>
                ) : (
                  <span>{breadcrumbCategory}</span>
                )}
              </>
            )}
            <span>/</span>
            <span className="text-stone-700">{title}</span>
          </nav>
          <h1 className="mb-3 text-3xl font-extrabold text-stone-900 md:text-4xl">
            {title}
          </h1>
          {subtitle && (
            <p className="mb-3 text-base text-stone-500 md:text-lg">
              {subtitle}
            </p>
          )}
          {lastUpdated && (
            <p className="text-xs text-stone-400 md:text-sm">
              {lastUpdatedLabel ?? "Last updated"}: {lastUpdated}
            </p>
          )}
        </div>
      </section>

      {/* Mobile TOC dropdown */}
      <div className="mx-auto max-w-5xl px-5 md:hidden">
        <button
          type="button"
          onClick={() => setMobileTocOpen((v) => !v)}
          className="flex w-full cursor-pointer items-center justify-between rounded-2xl border border-stone-200 bg-white px-4 py-3 text-left"
          aria-expanded={mobileTocOpen}
        >
          <span className="flex items-center gap-2 text-sm font-semibold text-stone-900">
            <List className="h-4 w-4 text-stone-400" aria-hidden />
            {tocLabel ?? "Contents"}
          </span>
          <ChevronDown
            className={`h-4 w-4 text-stone-400 transition-transform ${
              mobileTocOpen ? "rotate-180" : ""
            }`}
            aria-hidden
          />
        </button>
        {mobileTocOpen && (
          <nav className="mt-2 rounded-2xl border border-stone-200 bg-white p-2">
            {sections.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                onClick={() => setMobileTocOpen(false)}
                className={`block rounded-lg px-3 py-2 text-sm transition-colors hover:bg-stone-50 ${
                  activeId === s.id
                    ? "font-semibold text-home-orange_1"
                    : "text-stone-700"
                }`}
              >
                {s.title}
              </a>
            ))}
          </nav>
        )}
      </div>

      {/* Main content with desktop sticky TOC */}
      <div className="mx-auto max-w-5xl px-5 pb-12 md:px-10 md:pb-20">
        <div className="md:flex md:gap-10">
          {/* Desktop sticky TOC */}
          <aside className="hidden shrink-0 md:block md:w-56 lg:w-60">
            <nav
              className="sticky top-6 max-h-[calc(100vh-3rem)] overflow-y-auto py-2"
              aria-label="Table of contents"
            >
              <p className="mb-3 px-3 text-xs font-semibold uppercase tracking-wide text-stone-400">
                {tocLabel ?? "Contents"}
              </p>
              <ul className="space-y-0.5">
                {sections.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className={`block rounded-lg px-3 py-1.5 text-sm transition-colors ${
                        activeId === s.id
                          ? "bg-orange-50 font-semibold text-home-orange_1"
                          : "text-stone-600 hover:bg-stone-50 hover:text-stone-900"
                      }`}
                    >
                      {s.title}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </aside>

          {/* Sections */}
          <div className="min-w-0 flex-1 space-y-6 pt-6 md:pt-0">
            {sections.map((s) => (
              <section
                key={s.id}
                id={s.id}
                ref={(el) => {
                  if (el) sectionRefs.current.set(s.id, el)
                  else sectionRefs.current.delete(s.id)
                }}
                className="scroll-mt-6 rounded-2xl border border-stone-200 bg-white p-6 md:p-8"
              >
                <h2 className="mb-4 text-xl font-bold text-stone-900 md:text-2xl">
                  {s.title}
                </h2>
                <div
                  className="legal-prose text-sm leading-[1.75] text-stone-700 md:text-base"
                  dangerouslySetInnerHTML={{ __html: s.html }}
                />
              </section>
            ))}
          </div>
        </div>
      </div>

      {/* Contact footer */}
      <section>
        <div className="mx-auto max-w-5xl px-5 pb-16 md:px-10 md:pb-20">
          <h2 className="text-center text-2xl font-bold text-stone-900 md:text-3xl">
            {contact.title}
          </h2>
          <p className="mt-3 text-center text-stone-500">{contact.subtitle}</p>
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-3">
            <button
              type="button"
              onClick={() => {
                const w = window as unknown as {
                  $crisp?: { push: (args: unknown[]) => void }
                }
                if (w.$crisp) w.$crisp.push(["do", "chat:open"])
              }}
              className="flex cursor-pointer flex-col items-center rounded-2xl border border-stone-200 bg-white p-6 text-left transition-shadow hover:shadow-md"
            >
              <MessageCircle className="h-8 w-8 text-home-orange_1" />
              <p className="mt-4 text-base font-semibold text-stone-900">
                {contact.liveChat}
              </p>
              <p className="mt-1 text-xs text-stone-500">
                {contact.liveChatNote}
              </p>
            </button>
            <a
              href={waNumber ? `https://wa.me/${waNumber}` : "#"}
              target="_blank"
              rel="noopener noreferrer"
              className="flex flex-col items-center rounded-2xl border border-stone-200 bg-white p-6 transition-shadow hover:shadow-md"
            >
              <svg
                className="h-8 w-8 text-green-600"
                viewBox="0 0 24 24"
                fill="currentColor"
                aria-hidden
              >
                <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.594 5.351l-.999 3.648 3.894-.698zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.149-.173.198-.297.298-.495.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01a1.094 1.094 0 00-.793.372s-1.04 1.016-1.04 2.479c0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.711.306 1.264.489 1.696.626.713.226 1.361.194 1.874.118.572-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413z" />
              </svg>
              <p className="mt-4 text-base font-semibold text-stone-900">
                {contact.whatsApp}
              </p>
              <p className="mt-1 text-xs text-stone-500">
                {contact.whatsAppValue}
              </p>
            </a>
            <a
              href={`mailto:${contact.emailValue}`}
              className="flex flex-col items-center rounded-2xl border border-stone-200 bg-white p-6 transition-shadow hover:shadow-md"
            >
              <Mail className="h-8 w-8 text-stone-600" />
              <p className="mt-4 text-base font-semibold text-stone-900">
                {contact.email}
              </p>
              <p className="mt-1 text-xs text-stone-500">
                {contact.emailValue}
              </p>
            </a>
          </div>
        </div>
      </section>
    </div>
  )
}
